import { useCallback, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ISelectOption } from '@private/components';
import { useUpdateEffect } from '@private/hooks';
import i18n from 'configs/i18n';

import { LOCAL_SEARCH_KEY } from 'constants/common';
import { useCreateShortLink } from 'queries/sharedLink';
import { ROUTES } from 'routes/config/constants';
import { copyToClipBoard } from 'utils/common';
import { TSearch } from 'utils/types';

import { IFiltersConfig } from '../types';

import { getLikeConflictColumns, getOppositeLikeOperation } from './utils/helpers';
import {
  FILTER_OPERATIONS,
  IAdditionalCondition,
  IAppliedFilter,
  IAppliedFilterValue,
  IFilterValue,
  IOpenSidebarParams,
  ISelectFilterProps,
  ISelectOperationProps,
  IToggleAdditionalConditionProps,
  NOT_EQUALS_NULL_VALUE,
} from './utils/types';

import {
  IRemoveFilterParams,
  IToggleFilterEnabledParams,
  IUseFiltrationResult,
  TSidebarFocusedField,
  TUseFiltrationProps,
} from './types';

type TGetInitialFiltersProps = Pick<IFiltersConfig, 'ignoreList' | 'intlPrefix'> & {
  filters?: TSearch;
};

export const getAppliedFilters = ({
  filters,
  ignoreList,
  intlPrefix,
}: TGetInitialFiltersProps) => {
  if (!filters) {
    return {};
  }

  const entries = Object.entries(filters);

  if (!entries.length) {
    return {};
  }

  let isEmpty = true;

  const filtersList = entries.reduce<IAppliedFilter>((prev, [columnKey, value]) => {
    const isColumnIgnored = ignoreList?.includes(columnKey);
    const isLocalSearchKey = columnKey === LOCAL_SEARCH_KEY;
    const isI18nExist = i18n.exists(`${intlPrefix}.${columnKey}`);

    if (!isColumnIgnored && !isLocalSearchKey && isI18nExist && value) {
      prev[columnKey] = value;
      isEmpty = false;
    }

    return prev;
  }, {});

  if (isEmpty) {
    return {};
  }

  return filtersList;
};

export const getFilterValueByOperation = (
  filter: IAppliedFilterValue,
  operation: ISelectOperationProps['operation'],
): IFilterValue => {
  let { value } = filter;

  const changed = {
    fromEqualsOperations:
      filter.operation === FILTER_OPERATIONS.EQUALS ||
      filter.operation === FILTER_OPERATIONS.NOT_EQUALS,
    fromInOperations:
      filter.operation === FILTER_OPERATIONS.IN ||
      filter.operation === FILTER_OPERATIONS.NOT_IN,
    fromNullOperations:
      filter.operation === FILTER_OPERATIONS.EQUALS_NULL ||
      filter.operation === FILTER_OPERATIONS.NOT_EQUALS_NULL,
    fromRange:
      filter.operation === FILTER_OPERATIONS.RANGE &&
      operation !== FILTER_OPERATIONS.RANGE,
    toEqualsOperations:
      operation === FILTER_OPERATIONS.EQUALS ||
      operation === FILTER_OPERATIONS.NOT_EQUALS,
    toInOperations:
      operation === FILTER_OPERATIONS.IN || operation === FILTER_OPERATIONS.NOT_IN,
    toNullOperations:
      operation === FILTER_OPERATIONS.EQUALS_NULL ||
      operation === FILTER_OPERATIONS.NOT_EQUALS_NULL,
    toRange:
      filter.operation !== FILTER_OPERATIONS.RANGE &&
      operation === FILTER_OPERATIONS.RANGE,
  };

  if (changed.toNullOperations) {
    return NOT_EQUALS_NULL_VALUE;
  }

  if (
    (!changed.toInOperations && changed.fromInOperations) ||
    (changed.toInOperations && !changed.fromInOperations) ||
    (!changed.fromEqualsOperations && changed.toEqualsOperations) ||
    changed.fromRange ||
    changed.toRange ||
    changed.fromNullOperations
  ) {
    value = null;
  }

  return value;
};

const getCondition = (filter: IAppliedFilterValue, isAdditional?: boolean) =>
  isAdditional ? filter.additionalCondition : filter;

const setCondition = (
  filter: IAppliedFilterValue,
  isAdditional: boolean | undefined,
  condition: IAdditionalCondition,
): IAppliedFilterValue =>
  isAdditional
    ? { ...filter, additionalCondition: condition }
    : { ...filter, ...condition };

// Emptying the primary row promotes the opposite condition so the column survives
const withoutCondition = (
  filters: IAppliedFilter,
  columnKey: string,
  isAdditional?: boolean,
): IAppliedFilter => {
  const { [columnKey]: filter, ...restFilters } = filters;
  const { additionalCondition, ...primary } = filter || {};

  if (isAdditional) {
    return { ...restFilters, [columnKey]: primary };
  }

  if (additionalCondition) {
    return { ...restFilters, [columnKey]: additionalCondition };
  }

  return restFilters;
};

export const useFiltration = ({
  filtersConfig,
  gridColumnsSettings,
  onPageFiltersChange,
  pageFilters,
}: TUseFiltrationProps): IUseFiltrationResult => {
  const { fallbackValues, filtersSectionsMap, ignoreList, intlPrefix, sharableFilters } =
    filtersConfig || {};
  const { search: filters } = pageFilters || {};

  const { isPending: isCreatingShortLink, mutate: createShortLink } =
    useCreateShortLink();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarFilters, setSidebarFilters] = useState<IAppliedFilter>(
    getAppliedFilters({ filters, ignoreList, intlPrefix }),
  );
  const [focusedFieldName, setFocusedFieldName] = useState<TSidebarFocusedField>(null);
  const [canFilter, setCanFilter] = useState(false);

  useUpdateEffect(() => {
    setSidebarFilters(getAppliedFilters({ filters, ignoreList, intlPrefix }));
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    setCanFilter(true);

    if (!fallbackValues) {
      setSidebarFilters({});
    }

    const newFilters: IAppliedFilter = {};

    for (const columnKey in fallbackValues) {
      const fallbackValueCallback = fallbackValues?.[columnKey];

      if (fallbackValueCallback) {
        const fallbackFilter = fallbackValueCallback();

        newFilters[columnKey] = {
          ...sidebarFilters[columnKey],
          ...fallbackFilter,
        };
      }
    }

    setSidebarFilters(newFilters);
  }, [sidebarFilters, fallbackValues]);

  const clearSectionFilters = useCallback(
    (sectionName: string) => {
      setCanFilter(true);

      const newFiltersList: IAppliedFilter = {};

      for (const columnKey in sidebarFilters) {
        const filter = sidebarFilters[columnKey];
        const fallbackValueCallback = fallbackValues?.[columnKey];

        if (fallbackValueCallback) {
          const fallbackFilter = fallbackValueCallback();

          newFiltersList[columnKey] = {
            ...filter,
            ...fallbackFilter,
          };
          continue;
        }

        if (filtersSectionsMap?.[columnKey] !== sectionName) {
          newFiltersList[columnKey] = filter;
        }
      }

      setSidebarFilters(newFiltersList);
    },
    [sidebarFilters, fallbackValues, filtersSectionsMap],
  );

  const conflictColumns = useMemo(
    () => getLikeConflictColumns(sidebarFilters),
    [sidebarFilters],
  );

  const onApply = useCallback(
    (newFilters?: IAppliedFilter) => {
      const filtersToApply = newFilters || sidebarFilters;

      if (getLikeConflictColumns(filtersToApply).length) {
        return;
      }

      onPageFiltersChange?.({
        filters: filtersToApply,
        gridColumnsSettings,
      });
      setIsSidebarOpen(false);
      setCanFilter(false);
    },
    [sidebarFilters, gridColumnsSettings, onPageFiltersChange],
  );

  const toggleFilterEnabled = useCallback(
    ({ columnKey, index, isAdditional }: IToggleFilterEnabledParams) => {
      const filter = sidebarFilters[columnKey];
      const condition = filter && getCondition(filter, isAdditional);

      if (!filter || !condition) {
        return sidebarFilters;
      }

      setCanFilter(true);

      const isPerValueToggle =
        typeof index === 'number' && Array.isArray(condition.value);

      const updatedCondition: IAdditionalCondition = isPerValueToggle
        ? {
            ...condition,
            disabledIndexes: (condition.disabledIndexes ?? []).includes(index)
              ? condition.disabledIndexes?.filter((i) => i !== index)
              : [...(condition.disabledIndexes ?? []), index],
          }
        : { ...condition, enabled: condition.enabled === false };

      const newFilters = {
        ...sidebarFilters,
        [columnKey]: setCondition(filter, isAdditional, updatedCondition),
      };

      setSidebarFilters(newFilters);
      return newFilters;
    },
    [sidebarFilters],
  );

  const removeFilter = useCallback(
    ({ columnKey, index, isAdditional, resetCanFilter }: IRemoveFilterParams) => {
      if (resetCanFilter) {
        setCanFilter(true);
      }

      const fallbackValueCallback = columnKey && fallbackValues?.[columnKey];

      if (typeof index !== 'undefined') {
        const filter = sidebarFilters[columnKey];
        const condition = filter && getCondition(filter, isAdditional);

        if (!filter || !condition) {
          return sidebarFilters;
        }

        const values = Array.isArray(condition.value)
          ? (condition.value as ISelectOption[])
          : [condition.value as ISelectOption];
        const filtratedValues = values.filter((_, i) => i !== index);

        if (!filtratedValues.length) {
          const newFiltersList = withoutCondition(
            sidebarFilters,
            columnKey,
            isAdditional,
          );

          setSidebarFilters(newFiltersList);
          return newFiltersList;
        }

        const { disabledIndexes, ...restCondition } = condition;
        const reindexed = disabledIndexes
          ?.filter((i) => i !== index)
          .map((i) => (i > index ? i - 1 : i));
        const updatedCondition: IAdditionalCondition = reindexed?.length
          ? { ...restCondition, disabledIndexes: reindexed, value: filtratedValues }
          : { ...restCondition, value: filtratedValues };

        const newFiltersList = {
          ...sidebarFilters,
          [columnKey]: setCondition(filter, isAdditional, updatedCondition),
        };

        setSidebarFilters(newFiltersList);
        return newFiltersList;
      }

      if (isAdditional) {
        const newFiltersList = withoutCondition(sidebarFilters, columnKey, true);

        setSidebarFilters(newFiltersList);
        return newFiltersList;
      }

      const { [columnKey]: removedFilter, ...restSidebarFilters } = sidebarFilters;

      if (fallbackValueCallback) {
        const fallbackFilter = fallbackValueCallback();
        const newFiltersList = {
          ...restSidebarFilters,
          [columnKey]: {
            ...removedFilter,
            ...fallbackFilter,
          },
        };

        setSidebarFilters(newFiltersList);
        return newFiltersList;
      }

      setSidebarFilters(restSidebarFilters);
      return restSidebarFilters;
    },
    [sidebarFilters, fallbackValues],
  );

  const clearFocusedItem = useCallback(() => {
    if (focusedFieldName) {
      setFocusedFieldName(null);
    }
  }, [focusedFieldName]);

  const onSelectFilter = useCallback(
    ({ columnKey, immediateApply, isAdditional, ...rest }: ISelectFilterProps) => {
      setCanFilter(true);

      const fallbackValueCallback = fallbackValues?.[columnKey];

      let newFilters;

      if (isAdditional) {
        const filter = sidebarFilters[columnKey] || {};

        newFilters =
          rest.value === undefined
            ? withoutCondition(sidebarFilters, columnKey, true)
            : {
                ...sidebarFilters,
                [columnKey]: setCondition(filter, true, {
                  ...filter.additionalCondition,
                  ...rest,
                }),
              };
      } else if (rest.value === undefined && fallbackValueCallback) {
        const fallbackFilter = fallbackValueCallback();
        newFilters = {
          ...sidebarFilters,
          [columnKey]: {
            ...sidebarFilters[columnKey],
            ...fallbackFilter,
          },
        };
      } else if (rest.value === undefined && !fallbackValueCallback) {
        const { [columnKey]: _, ...oldFilters } = sidebarFilters;
        newFilters = oldFilters;
      } else {
        newFilters = {
          ...sidebarFilters,
          [columnKey]: {
            ...sidebarFilters[columnKey],
            ...rest,
          },
        };
      }

      setSidebarFilters(newFilters);

      if (immediateApply) {
        onApply(newFilters);
      }
    },
    [sidebarFilters, fallbackValues, onApply],
  );

  const onSelectOperation = useCallback(
    ({ columnKey, operation }: ISelectOperationProps) => {
      setCanFilter(true);

      const filter = sidebarFilters[columnKey] || {};
      const newValue = getFilterValueByOperation(filter, operation);

      const newFilters = {
        ...sidebarFilters,
        [columnKey]: {
          operation,
          value: newValue,
        },
      };
      setSidebarFilters(newFilters);
    },
    [sidebarFilters],
  );

  const toggleAdditionalCondition = useCallback(
    ({ columnKey, operation }: IToggleAdditionalConditionProps) => {
      setCanFilter(true);

      const { additionalCondition, ...filter } = sidebarFilters[columnKey] || {};

      setSidebarFilters({
        ...sidebarFilters,
        [columnKey]: additionalCondition
          ? filter
          : {
              ...filter,
              additionalCondition: {
                operation: getOppositeLikeOperation(operation),
                value: [''],
              },
              operation,
            },
      });
    },
    [sidebarFilters],
  );

  const onCloseSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const onOpenSidebar = useCallback(
    ({ columnKey, index, isAdditional }: IOpenSidebarParams) => {
      setIsSidebarOpen(true);

      if (columnKey) {
        const currentFilter = sidebarFilters[columnKey];
        const condition = currentFilter && getCondition(currentFilter, isAdditional);
        const fieldIndex =
          index === undefined && Array.isArray(condition?.value)
            ? condition.value.length - 1
            : index;

        setFocusedFieldName({
          columnKey,
          index: fieldIndex,
          isAdditional,
        });
      }
    },
    [sidebarFilters],
  );

  const onToggleSidebar = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen]);

  const onShareFilters = useCallback(async () => {
    const { origin, pathname } = window.location;

    const linkToShare = `${pathname}?${new URLSearchParams({
      ...sharableFilters,
      pageFilters: JSON.stringify({
        columns: gridColumnsSettings,
        search: sidebarFilters,
      }),
    }).toString()}`;

    createShortLink(
      { sharedLink: linkToShare },
      {
        onSuccess: async ({ result: shortLink }) => {
          const { PARAMS, PATH } = ROUTES.SHORT_LINK;
          const shortLinkPath = generatePath(PATH, { [PARAMS.CODE]: shortLink });
          await copyToClipBoard(`${origin}${shortLinkPath}`);
        },
      },
    );
  }, [sharableFilters, gridColumnsSettings, sidebarFilters, createShortLink]);

  return {
    canFilter,
    conflictColumns,
    focusedFieldName,
    isCreatingShortLink,
    isSidebarOpen,
    setCanFilter,
    setFocusedFieldName,
    setIsSidebarOpen,
    setSidebarFilters,
    sidebarFilters,

    clearAllFilters,
    clearFocusedItem,
    clearSectionFilters,
    onApply,
    onCloseSidebar,
    onOpenSidebar,
    onSelectFilter,
    onSelectOperation,
    onShareFilters,
    onToggleSidebar,
    removeFilter,
    toggleAdditionalCondition,
    toggleFilterEnabled,
  };
};
